import React from "react";
import { Route, Routes } from "react-router-dom";
import AccountingPage from "../pages/AccountingPage";
import AccountingOnlineSalesReportPage from "../pages/AccountingOnlineSalesReportPage";
import AccountingPnlPage from "../pages/AccountingPnlPage";
import AccountingPnlBreakdownPage from "../pages/AccountingPnlBreakdownPage";
import AccountingWholesaleSalesPage from "../pages/AccountingWholesaleSalesPage";
import { RequireStaff } from "../components/RequireStaff";
import AuthenticatedLayout from "../layouts/AuthenticatedLayout";
import DashboardPage from "../pages/DashboardPage";
import HomePage from "../pages/HomePage";
import LoginPage from "../pages/LoginPage";
import NotFoundPage from "../pages/NotFoundPage";
import OnlineSalesPage from "../pages/OnlineSalesPage";
import OnlineSellerPage from "../pages/OnlineSellerPage";
import SettingsPage from "../pages/SettingsPage";
import WholesaleCustomersPage from "../pages/WholesaleCustomersPage";
import WholesaleCustomerDetailPage from "../pages/WholesaleCustomerDetailPage";
import WholesaleSalesListPage from "../pages/WholesaleSalesListPage";
import WholesaleSalePage from "../pages/WholesaleSalePage";
import WarehousePage from "../pages/WarehousePage";
import WarehouseRevisionPage from "../pages/WarehouseRevisionPage";
import WarehouseRevisionCreatePage from "../pages/WarehouseRevisionCreatePage";
import WarehouseRevisionDetailPage from "../pages/WarehouseRevisionDetailPage";
import WarehouseRevisionFinanceKindPage from "../pages/WarehouseRevisionFinanceKindPage";
import WarehouseRevisionReportsPage from "../pages/WarehouseRevisionReportsPage";
import WarehouseRevisionEditPage from "../pages/WarehouseRevisionEditPage";
import StockTransfersPage from "../pages/StockTransfersPage";
import StockTransferDetailPage from "../pages/StockTransferDetailPage";
import StockTransferCreatePage from "../pages/StockTransferCreatePage";
import StockTransferEditPage from "../pages/StockTransferEditPage";
import PurchaseRequestsPage from "../pages/PurchaseRequestsPage";
import PurchaseRequestDetailPage from "../pages/PurchaseRequestDetailPage";
import PurchaseRequestCreatePage from "../pages/PurchaseRequestCreatePage";
import PurchaseRequestEditPage from "../pages/PurchaseRequestEditPage";
import ProductsPage from "../pages/ProductsPage";
import StockOverviewPage from "../pages/StockOverviewPage";
import ReturnsPage from "../pages/ReturnsPage";
import ProductEditPage from "../pages/ProductEditPage";
import ProductCreatePage from "../pages/ProductCreatePage";
import CategoriesPage from "../pages/CategoriesPage";
import CategoryCreatePage from "../pages/CategoryCreatePage";
import CategoryEditPage from "../pages/CategoryEditPage";
import StoresPage from "../pages/StoresPage";
import StoreSalesPage from "../pages/StoreSalesPage";
import StoreDetailPage from "../pages/StoreDetailPage";
import StoreShiftStatusPage from "../pages/StoreShiftStatusPage";
import RolesPage from "../pages/RolesPage";
import RolePermissionsPage from "../pages/RolePermissionsPage";
import UsersPage from "../pages/UsersPage";
import UserEditPage from "../pages/UserEditPage";

import { RequirePermission } from "../components/RequirePermission";

/**
 * Роуты CRM и разделы матрицы прав (`access/registry.py`), которые они требуют.
 *
 * Доступ к странице — право «Просмотр» на раздел; кнопки «Создать / Изменить / Удалить»
 * страницы проверяют сами через `can()`. Раньше здесь были списки групп на каждом роуте,
 * а часть роутов (`/sales/online`, `/wholesale`) не проверялась вовсе (AUFB-87).
 */
const PROTECTED_ROUTES = [
  { path: "/", resource: "home", element: <HomePage /> },
  { path: "/sales", resource: "sales_dashboard", element: <DashboardPage /> },
  { path: "/sales/online", resource: "online_sales", element: <OnlineSalesPage /> },
  { path: "/sales/online/seller", resource: "online_seller", element: <OnlineSellerPage /> },
  { path: "/wholesale", resource: "wholesale_customer", element: <WholesaleCustomersPage /> },
  { path: "/wholesale/:customerId", resource: "wholesale_customer", element: <WholesaleCustomerDetailPage /> },
  { path: "/wholesale/sales", resource: "wholesale_sale", element: <WholesaleSalesListPage /> },
  { path: "/wholesale/sales/:saleId", resource: "wholesale_sale", element: <WholesaleSalePage /> },
  { path: "/accounting", resource: "accounting", element: <AccountingPage /> },
  { path: "/accounting/pnl", resource: "report_pnl", element: <AccountingPnlPage /> },
  { path: "/accounting/pnl/by-stores", resource: "report_pnl", element: <AccountingPnlBreakdownPage kind="stores" /> },
  { path: "/accounting/pnl/by-categories", resource: "report_pnl", element: <AccountingPnlBreakdownPage kind="categories" /> },
  { path: "/accounting/wholesale-sales", resource: "report_wholesale", element: <AccountingWholesaleSalesPage /> },
  { path: "/accounting/online-sales", resource: "report_online", element: <AccountingOnlineSalesReportPage /> },
  { path: "/management/store-shifts", resource: "store_shifts", element: <StoreShiftStatusPage /> },
  { path: "/management/roles", resource: "role", element: <RolesPage /> },
  { path: "/management/roles/:roleId", resource: "role", element: <RolePermissionsPage /> },
  { path: "/management/users", resource: "user", element: <UsersPage /> },
  { path: "/management/users/:userId", resource: "user", element: <UserEditPage /> },
  { path: "/warehouse", resource: "warehouse_dashboard", element: <WarehousePage /> },
  { path: "/warehouse/products", resource: "product", element: <ProductsPage /> },
  { path: "/warehouse/products/new", resource: "product", element: <ProductCreatePage /> },
  { path: "/warehouse/products/:productId/edit", resource: "product", element: <ProductEditPage /> },
  { path: "/warehouse/stock-overview", resource: "stock", element: <StockOverviewPage /> },
  { path: "/warehouse/returns", resource: "returns", element: <ReturnsPage /> },
  { path: "/warehouse/stores", resource: "store", element: <StoresPage /> },
  { path: "/warehouse/stores/:storeId", resource: "store", element: <StoreDetailPage /> },
  { path: "/warehouse/stores/:storeId/sales", resource: "store", element: <StoreSalesPage /> },
  { path: "/warehouse/categories", resource: "category", element: <CategoriesPage /> },
  { path: "/warehouse/categories/new", resource: "category", element: <CategoryCreatePage /> },
  { path: "/warehouse/categories/:categoryId/edit", resource: "category", element: <CategoryEditPage /> },
  { path: "/warehouse/revision", resource: "inventory_revision", element: <WarehouseRevisionPage /> },
  { path: "/warehouse/revision/new", resource: "inventory_revision", element: <WarehouseRevisionCreatePage /> },
  { path: "/warehouse/revision/:revisionId", resource: "inventory_revision", element: <WarehouseRevisionDetailPage /> },
  { path: "/warehouse/revision/:revisionId/edit", resource: "inventory_revision", element: <WarehouseRevisionEditPage /> },
  { path: "/warehouse/revision/:revisionId/finance/:kind", resource: "inventory_revision", element: <WarehouseRevisionFinanceKindPage /> },
  { path: "/warehouse/revision/reports", resource: "revision_report", element: <WarehouseRevisionReportsPage /> },
  { path: "/warehouse/transfers", resource: "stock_transfer", element: <StockTransfersPage /> },
  { path: "/warehouse/transfers/new", resource: "stock_transfer", element: <StockTransferCreatePage /> },
  { path: "/warehouse/transfers/:transferId", resource: "stock_transfer", element: <StockTransferDetailPage /> },
  { path: "/warehouse/transfers/:transferId/edit", resource: "stock_transfer", element: <StockTransferEditPage /> },
  { path: "/warehouse/purchase-requests", resource: "purchase_request", element: <PurchaseRequestsPage /> },
  { path: "/warehouse/purchase-requests/new", resource: "purchase_request", element: <PurchaseRequestCreatePage /> },
  { path: "/warehouse/purchase-requests/:requestId", resource: "purchase_request", element: <PurchaseRequestDetailPage /> },
  { path: "/warehouse/purchase-requests/:requestId/edit", resource: "purchase_request", element: <PurchaseRequestEditPage /> },
];

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth/login" element={<LoginPage />} />
      <Route
        element={
          <RequireStaff>
            <AuthenticatedLayout />
          </RequireStaff>
        }
      >
        {PROTECTED_ROUTES.map(({ path, resource, element }) => (
          <Route key={path} path={path} element={<RequirePermission resource={resource}>{element}</RequirePermission>} />
        ))}
        {/* Свой профиль доступен всем, у кого есть вход в CRM. */}
        <Route path="/settings" element={<SettingsPage />} />
        {/* 404 внутри layout: сайдбар остаётся, есть куда уйти.
            Неавторизованного RequireStaff уведёт на логин раньше. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
